<template>
  <div :class="['relative content']" @click="handleEnterClick">
    <ZhongQIu
      v-if="isFestival === 'zhongqiu'"
      :loading="controlLoading"
      :customText="customText"
      :disabledEnter="disabledEnter"
      :popoverVisible.sync="popoverVisible"
      @menu="$emit('menu')"
      @setting="$emit('setting')"
      @relearn="handleClearButton"
      @openReport="$emit('openReport')"
    />
    <YuanDan
      v-else-if="isFestival === 'yuandan'"
      :loading="controlLoading"
      :customText="customText"
      :disabledEnter="disabledEnter"
      :popoverVisible.sync="popoverVisible"
      @menu="$emit('menu')"
      @setting="$emit('setting')"
      @relearn="handleClearButton"
      @openReport="$emit('openReport')"
    />
    <Common
      v-else
      :loading="controlLoading"
      :customText="customText"
      :disabledEnter="disabledEnter"
      :popoverVisible.sync="popoverVisible"
      @menu="$emit('menu')"
      @setting="$emit('setting')"
      @relearn="handleClearButton"
      @openReport="$emit('openReport')"
    />
  </div>
</template>

<script lang="ts">
import { Content, Sequence } from '@forchange/storytime';
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { throttle } from '@/utils/throttle';
import MenuIcom from './assets/MenuIcon.vue';
import SettingIcon from './assets/SettingIcon.vue';
import LoadingControl from './assets/LoadingControl.vue';
import Dialog from '@/components/dialog';
import EnterIcon from './assets/EnterIcon.vue';
import { namespace } from 'vuex-class';

import Common from './common/index.vue';
import YuanDan from './yuandan/index.vue';
import ZhongQIu from './zhongqiu/index.vue';
import { Festival } from '@/store/modules/common/types';
import { isBoolean } from 'lodash';
import { setDocumentTitle } from '@/utils/common';

const userModule = namespace('common');

const limitTime = 650;
@Component({
  components: {
    MenuIcom,
    SettingIcon,
    LoadingControl,
    Dialog,
    EnterIcon,
    Common,
    YuanDan,
    ZhongQIu
  }
})
export default class UtteranceControl extends Vue {
  @Prop() content!: Content & {
    messages?: Array<string>;
    index: number;
  };
  @Prop({ type: Boolean }) isLoading!: boolean;
  @Prop({ type: Boolean, default: false }) disabledEnter!: boolean;
  @Prop({ type: Boolean, default: false }) customEndter!: boolean;
  @Prop({ type: String }) customText!: string;
  @Prop({ type: Number }) utteranceInterval!: number;
  @Prop({ type: Boolean, default: false }) isSelfEnter!: boolean;
  @Prop() autoPlayConfig?: {
    delay?: number;
  };

  @userModule.State('loading') globalLoading!: boolean;
  @userModule.State('isFestival') isFestival!: Festival;

  popoverVisible = false;
  dialogVisible = false;

  isAutoEnter: undefined | boolean;

  get portalView() {
    const portalSequence = this.$store.state.common.portalSequence;
    return portalSequence[portalSequence.length - 1];
  }

  get controlLoading() {
    const loading =
      this.loading || !!this.utteranceInterval || (!this.disabledEnter && !!this.autoPlayConfig);
    let title = '正在输入中...';
    if (!loading) {
      title =
        (window.storyteller.topSequence as Sequence & { title: string })?.title || '风变课灵小馆';
    }
    setDocumentTitle(title);
    this.$PostConnection?.command('change-title', {
      protocol: 'cleword-system',
      command: 'change-title',
      data: title
    });
    return loading;
  }

  loading = false;
  handleEnter = throttle(
    () => {
      this.next();
    },
    limitTime,
    this.handleLoading,
    this.handleCloseLoading
  );

  handleEnterClick(isAutoEnter?: boolean) {
    if (isBoolean(isAutoEnter)) this.isAutoEnter = isAutoEnter;
    if (this.disabledEnter) return this.$emit('disabledEnter');
    if (this.customEndter)
      return this.$emit('enter', {
        type: 'click'
      });
    this.handleEnter();
  }

  handleLoading() {
    this.loading = true;
  }

  handleCloseLoading() {
    this.loading = false;
  }

  handleClearButton(el: Event) {
    this.popoverVisible = false;
    this.$Modal.confirm({
      type: 'wran',
      title: '确定要重新学习？',
      descript: '将会清空当前课程的所有学习记录',
      confirmText: '确定',
      onOk: () => {
        this.$emit('clear');
      }
    });
  }

  next() {
    if (this.portalView) return this.$emit('back');
    if (this.globalLoading) return;
    if (this.controlLoading && !this.isAutoEnter) return;
    this.isAutoEnter = undefined;
    this.$emit('next');
    const { messages, index = 0 } = this.content;
    if (messages && index < messages.length - 1) {
      this.updateContentIndex();
      return;
    }
    window.next();
  }

  updateContentIndex() {
    window.storyteller.updateContent((content) => ({
      index: (this.content.index || 0) + 1,
      touchLoading: true,
      // 清除笔记中标记
      takingNotes: -1
    }));
  }

  private onKeyboard() {
    document.addEventListener('keyup', this.onKeyPress);
    this.$once('hook:beforeDestroy', () => {
      document.removeEventListener('keyup', this.onKeyPress);
    });
  }

  private onKeyPress(event: KeyboardEvent) {
    // 图片左右操作互斥
    if (document.querySelector('.pswp')) return;
    if ((event.target as HTMLElement).matches('textarea') || this.isLoading) return;
    if (this.disabledEnter && !this.isSelfEnter) return this.$emit('disabledEnter');
    switch (event.code) {
      case 'Enter':
      case 'NumpadEnter':
      case 'Space':
      case 'ArrowDown':
      case 'ArrowRight':
        if (this.customEndter)
          return this.$emit('enter', {
            type: 'keyup'
          });
        this.handleEnter();
        break;
    }
  }

  initGlobalAutoPlay() {
    if (!this.autoPlayConfig) return;
    const autoPlayTimmer = window.setInterval(() => {
      if (this.disabledEnter) return;
      this.handleLoading();
      this.handleEnterClick(true);
    }, (this.autoPlayConfig.delay || 3) * 1000);
    this.$once('hook:beforeDestroy', () => {
      window.clearInterval(autoPlayTimmer);
    });
  }

  mounted() {
    this.onKeyboard();
    this.initGlobalAutoPlay();
  }
}
</script>

<style scoped>
.content {
  -webkit-tap-highlight-color: transparent;
  @apply flex justify-center h-16 cursor-pointer;
}
</style>
